import { Row, Space } from "antd";
import { ColumnProps } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Button } from "Components/General/Button";
import { dayjs } from "Config/dayjs";
import { API } from "General/API";
import { projectsDataStore } from "Stores";
import { fileDetailsStore } from "../FileDetailsStore";
import { API_URL } from "General/APIEndpoints";
import { download } from "utils/download";
import { EmailDetailsModal } from "./EmailDetailsModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { stringSort, dateSort } from "utils/defaultSort";
import { withModal } from "Components/General/Modal/withModal";
import { Table } from "Components/General/Table";
import { useEffect, useState } from "react";

interface FileDataEntityWithUid extends API_FileDataEntity {
  uid: string | number;
}

export const Information = withModal(({ showModal }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [data, setData] = useState<FileDataEntityWithUid[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFetch = async () => {
    try {
      setLoading(true);
      const response = await API.getData<API_FileDataEntity[]>(
        API_URL.FILES.getFileAccessHistory(projectsDataStore.projectId, fileDetailsStore.fileId)
      );
      setData(response.map((entry, idx) => ({ ...entry, uid: idx })));
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      const file: File = await API.getBlob(
        API_URL.FILES.getFileExcelReport(projectsDataStore.projectId, fileDetailsStore.fileId)
      );
      download(file, file.name, file.type);
    } catch (error) {
      throw error;
    }
  };

  const exportPdf = async () => {
    try {
      const file: File = await API.getBlob(
        API_URL.FILES.getFilePdfReport(projectsDataStore.projectId, fileDetailsStore.fileId)
      );
      download(file, file.name, file.type);
    } catch (error) {
      throw error;
    }
  };

  const showEmailDetails = (record: API_AccessHistoryEntry) => {
    showModal(EmailDetailsModal, {
      modalProps: {
        footer: null,
        width: `100%`,
        style: { maxWidth: 1200 },
        styles: { body: { paddingTop: 50 } },
      },
      componentProps: {
        emailId: record.mailId,
      },
    });
  };

  const expandedRowRender = (data: API_FileDataEntity) => {
    const columns: ColumnProps<API_AccessHistoryEntry>[] = [
      {
        dataIndex: "userNameAndCompany",
        title: t("User"),
        sorter: (a, b) => stringSort(a, b, "userNameAndCompany"),
      },
      {
        dataIndex: "firstInformation",
        title: t("Info received"),
        render: (firstInformation, rec) => (
          <>
            {firstInformation ? dayjs(+firstInformation).format("lll") : null}
            {rec.mailAllowed && (
              <span onClick={() => showEmailDetails(rec)} style={{ padding: "5px 10px", cursor: "pointer" }}>
                <FontAwesomeIcon icon={["far", "envelope"]} fixedWidth={true} />
              </span>
            )}
          </>
        ),
        sorter: (a, b) => dateSort(a, b, "firstInformation"),
      },
      {
        dataIndex: "firstAccess",
        title: t("Document received"),
        render: (firstAccess, rec) => (firstAccess ? `${dayjs(+firstAccess).format("lll")} (${rec.type})` : null),
        defaultSortOrder: "descend",
        sorter: (a, b) => dateSort(a, b, "firstAccess"),
      },
    ];
    return (
      <Table
        name="fileInfoExpanded"
        columns={columns}
        rowKey={(rec) => (Number(rec.userId.length) + Math.random()).toString(36)}
        dataSource={data.accessHistory}
        pagination={false}
      />
    );
  };

  useEffect(() => {
    handleFetch();
  }, []);

  const columns: ColumnProps<API_FileDataEntity>[] = [
    {
      dataIndex: "name",
      title: t("Filename"),
    },
    {
      dataIndex: "index",
      title: t("Index"),
      sorter: (a, b) => stringSort(a, b, "index"),
    },
    {
      dataIndex: "created",
      title: t("Time of upload"),
      render: (text) => dayjs(+text).format("lll"),
      defaultSortOrder: "descend",
      sorter: (a, b) => dateSort(a, b, "created"),
    },
  ];
  return (
    <>
      <Row className="mb-2" justify="end">
        <Space.Compact>
          <Button faIcon={["far", "file-excel"]} onClick={exportExcel}>
            {t("Export as Excel")}
          </Button>
          <Button faIcon={["far", "file-pdf"]} onClick={exportPdf}>
            {t("Export as PDF")}
          </Button>
        </Space.Compact>
      </Row>
      <Table<API_FileDataEntity>
        name="fileInfo"
        dataSource={data}
        loading={loading}
        rowKey="uid"
        columns={columns}
        expandable={{
          expandedRowRender: expandedRowRender,
        }}
        pagination={{ hideOnSinglePage: true }}
      />
    </>
  );
});
